import { provenanceDeRecette, type Provenance } from '@mlc/shared';
import { FOCUS_RING } from './states';

/**
 * Marque de provenance (US-74).
 *
 * ### Ce qu'elle dit, et ce qu'elle ne dit pas
 *
 * Elle dit **d'où vient la recette**, rien d'autre. L'étiquette calculée
 * « procédé en vidéo » dit **ce qui manque**. Les deux coïncident souvent et ne
 * sont pas la même chose : une recette Instagram dont on a écrit les étapes
 * après cuisson garde sa provenance et perd son étiquette. Les confondre
 * rendrait la marque menteuse au premier usage.
 *
 * ### Pourquoi pas le logo de marque
 *
 * L'argument est déjà écrit dans `ui/RecipeThumb.tsx` : « vingt vignettes côte
 * à côte doivent rester une bibliothèque, pas un nuancier ». Vingt dégradés
 * violets sur une grille de photographies culinaires produiraient exactement ce
 * que la refonte éditoriale a passé six passes à retirer. Le précédent existe
 * aussi : l'emoji 🎙 du mode cuisine est devenu un pictogramme tracé (§4
 * proscrit les emojis en guise d'icône). La marque suit le même chemin — et la
 * version monochrome est celle que les règles d'usage de Meta autorisent sans
 * retouche.
 *
 * ### Pourquoi elle n'est pas dans `ui/Icon.tsx`
 *
 * `Icon` est le **langage de l'application** : une liste fermée de gestes et de
 * destinations, où chaque nom est écrit à la main par un appelant. Ici le
 * dessin est choisi par une **donnée** (`source_name`, ce que le raccourci
 * d'US-72 écrit), et la table doit pouvoir grandir d'une ligne sans faire
 * grossir l'union `IconName` de noms que personne n'invoque. C'est la même
 * séparation qu'entre une icône et une photo : l'une appartient à l'interface,
 * l'autre au contenu.
 *
 * ### Pourquoi elle n'est pas posée à même la photo
 *
 * Le plan la voulait « dans l'angle de la vignette » **et** « monochrome,
 * tracée à `currentColor` », **et** couverte par un test de contraste. Les
 * trois ne tiennent pas ensemble : une marque posée sur une photographie
 * quelconque n'a aucun contraste calculable — la photo peut être blanche. Elle
 * est donc dans l'angle de la vignette, mais **sur une pastille opaque** faite
 * de jetons (`surface-raised` / `foreground-secondary`), exactement comme les
 * puces de la carte. Le contraste redevient une propriété de la feuille de
 * style, donc mesurable en clair comme en sombre
 * (`server/test/client/contrast.test.ts`), et le glyphe hérite bien de la
 * couleur du texte de sa pastille.
 */

/** Le glyphe d'une source. Tracé dans une boîte de 20, comme `ui/Icon.tsx`. */
const GLYPHES: Record<Provenance['cle'], string> = {
  // Le cadre arrondi, l'objectif et le témoin : la marque d'Instagram réduite à
  // son trait. Trois sous-chemins d'un seul `d`, comme les icônes composées de
  // `ui/Icon.tsx`.
  instagram:
    'M6.2 3.5 H13.8 A2.7 2.7 0 0 1 16.5 6.2 V13.8 A2.7 2.7 0 0 1 13.8 16.5 H6.2 A2.7 2.7 0 0 1 3.5 13.8 V6.2 A2.7 2.7 0 0 1 6.2 3.5 Z '
    + 'M10 6.9 A3.1 3.1 0 1 1 9.99 6.9 Z '
    + 'M13.9 6.05 H13.91',
};

export interface SourceMarkProps {
  /** La recette, telle que l'API la rend. Seules le titre, la source et son adresse sont lus. */
  recipe: { title: string; source_name: string | null; source_url: string | null };
  className?: string;
}

/** La pastille et son glyphe, sans ce qui l'entoure — le seul dessin, en un endroit. */
function Pastille({ cle }: { cle: Provenance['cle'] }): React.JSX.Element {
  return (
    <span className="inline-flex size-7 items-center justify-center rounded-full bg-surface-raised text-foreground-secondary shadow-card">
      <svg
        viewBox="0 0 20 20"
        className="size-4"
        aria-hidden="true"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={GLYPHES[cle]} />
      </svg>
    </span>
  );
}

/**
 * `null` quand la source n'est pas reconnue — c'est le cas le plus fréquent, et
 * c'est voulu : une recette saisie à la main ne porte aucune marque, et une
 * marque « source inconnue » serait du bruit sur toute la bibliothèque.
 *
 * **Cliquable quand la recette porte un lien.** Une marque qui dit « ça vient
 * d'Instagram » et ne mène pas à Instagram fait faire un détour par la fiche
 * pour un geste qu'on avait déjà en tête. Elle s'ouvre hors de l'application,
 * comme partout ailleurs dans cette story.
 *
 * Deux conséquences, toutes deux imposées :
 *
 * - **elle ne peut pas vivre dans la carte** : la carte *est* un lien, et un
 *   lien dans un lien n'est pas du HTML valide. Elle est posée à côté et
 *   superposée par positionnement, exactement comme le raccourci de cuisson
 *   d'US-58, qui occupe l'angle opposé pour la même raison ;
 * - **sa cible fait 44 px** (§4, plancher général), alors que la pastille en
 *   fait 28. La boîte est donc transparente et plus grande que le dessin : le
 *   poids visuel ne bouge pas, la cible est celle du doigt.
 *
 * Le nom accessible porte le titre — « Revoir « Ratatouille » sur Instagram » —
 * sans quoi une liste de liens annoncerait trente fois « Revoir sur
 * Instagram », le défaut qu'US-58 avait déjà rencontré.
 */
export function SourceMark({ recipe, className = '' }: SourceMarkProps): React.JSX.Element | null {
  const provenance = provenanceDeRecette(recipe);
  if (provenance === null) return null;

  if (recipe.source_url !== null) {
    const libelle = `Revoir « ${recipe.title} » sur ${provenance.nom}`;
    return (
      <a
        data-testid={`source-mark-${provenance.cle}`}
        href={recipe.source_url}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={libelle}
        title={libelle}
        className={`inline-flex size-touch-min items-center justify-center no-underline ${FOCUS_RING} ${className}`.trim()}
      >
        <Pastille cle={provenance.cle} />
      </a>
    );
  }

  return (
    <span
      data-testid={`source-mark-${provenance.cle}`}
      // Le sens vient du nom accessible, jamais du dessin (§4). « Vient
      // d'Instagram » et non « Instagram » : sur une carte qui porte déjà un
      // titre et des puces, un nom de marque seul ne dit pas ce qu'il fait là.
      role="img"
      aria-label={`Vient d’${provenance.nom}`}
      title={`Vient d’${provenance.nom}`}
      className={`inline-flex size-touch-min items-center justify-center ${className}`.trim()}
    >
      <Pastille cle={provenance.cle} />
    </span>
  );
}
